import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { familyManager } from '../core/FamilyManager';
import { BIMElement } from '../core/types';
import i18n from '../i18n';

/**
 * Place Static Family Tool
 *
 * Generic click-to-place for baked-asset families (IFC imports). Mirrors
 * ComponentTool: ray/plane pick + grid snap, but the element references the
 * family's staticAsset instead of carrying dimensions. The family to place
 * is armed through `armStaticFamilyPlacement` (Project Browser "Place"
 * command or the IFC import flow).
 */

let armedFamily: string | null = null;

/** Set the family the next PLACE_STATIC_FAMILY activation will place. */
export function armStaticFamilyPlacement(familyName: string): void {
    armedFamily = familyName;
}

export function getArmedStaticFamily(): string | null {
    return armedFamily;
}

/**
 * Build the BIMElement for one static family placement. Shared by the tool
 * and the canvas-side drop bridge so both produce identical payloads.
 * Returns null when the family is missing or carries no baked asset.
 */
export function buildStaticFamilyElement(
    familyName: string,
    position: [number, number, number],
    rotationDeg = 0
): BIMElement | null {
    const family = familyManager.getFamily(familyName);
    const staticAsset = family?.geometrySchema?.staticAsset;
    if (!family || !staticAsset || staticAsset.kind !== 'ifc') return null;

    const parameters: Record<string, any> = {
        ...(family.instanceParams ?? {}),
        'Rotation': `${rotationDeg.toFixed(2)}°`,
    };

    return {
        id: uuidv4(),
        name: family.name,
        category: family.category,
        type: `${family.name}: Default`,
        parameters,
        constraints: [],
        geometryData: {
            kind: 'ifc-static',
            assetId: staticAsset.assetId,
            position,
            rotation: rotationDeg,
        },
    } as unknown as BIMElement;
}

export class PlaceStaticFamilyTool extends BaseTool {
    id = 'PLACE_STATIC_FAMILY';
    name = 'Place Family';
    cursor = 'crosshair';

    private rotation = 0;
    private currentPreviewPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const familyName = armedFamily;
        if (!familyName) {
            this.notify(i18n.t('tools.notify.noFamilyArmedForPlacement'), 'warning');
            this.exitToSelect();
            return;
        }
        const family = familyManager.getFamily(familyName);
        const staticAsset = family?.geometrySchema?.staticAsset;
        if (!staticAsset || staticAsset.kind !== 'ifc') {
            this.notify(i18n.t('tools.notify.familyNoGeometry', { name: familyName }), 'error');
            this.exitToSelect();
            return;
        }

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        const parameters: Record<string, any> = {
            ...(family.instanceParams ?? {}),
            'Rotation': `${this.rotation.toFixed(2)}°`,
        };

        const element: any = {
            id: uuidv4(),
            name: family.name,
            category: family.category,
            type: `${family.name}: Default`,
            parameters,
            constraints: [],
            geometryData: {
                kind: 'ifc-static',
                assetId: staticAsset.assetId,
                position: [snapped.x, snapped.y, snapped.z],
                rotation: this.rotation,
            },
        };

        this.context.bimStore.addElement(element);
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPreviewPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.code === 'Space') {
            event.preventDefault();
            this.rotation = (this.rotation + 90) % 360;
            this.updatePreview();
        } else {
            super.onKeyDown(event);
        }
    }

    onPointerUp(): void { }

    deactivate(): void {
        super.deactivate();
    }

    private updatePreview() {
        if (!this.context || !this.currentPreviewPoint) return;
        this.context.appStore.setToolPreview({
            type: 'component',
            position: [this.currentPreviewPoint.x, this.currentPreviewPoint.y, this.currentPreviewPoint.z],
            rotation: THREE.MathUtils.degToRad(this.rotation),
        });
    }
}
